import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, functions } from '../firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Alert from 'react-bootstrap/Alert';
import NavBar from '../Components/NavBar';

const ReviewTeamRequests = () => {
  const [user, setUser] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionRequestId, setActionRequestId] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Only allow when 'admin' team is selected in localStorage
    const currentTeam = window.localStorage.getItem('currentTeam');
    if (currentTeam !== 'admin') {
      setIsAuthorized(false);
      setLoading(false);
      return () => {};
    }

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        loadRequests();
      } else {
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const loadRequests = async () => {
    setError('');
    setLoading(true);
    setIsAuthorized(true);
    try {
      const listPending = httpsCallable(functions, 'listTeamRequests');
      const result = await listPending();
      setRequests(result.data.requests || []);
    } catch (err) {
      if (err.code === 'permission-denied') {
        setIsAuthorized(false);
        setError('You are not authorized to review team requests. Please sign in with an admin email.');
      } else {
        setError(err.message || 'Unable to load team requests.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId) => {
    setError('');
    setActionRequestId(requestId);
    try {
      const approveRequest = httpsCallable(functions, 'approveTeamRequest');
      await approveRequest({ requestId });
      await loadRequests();
    } catch (err) {
      setError(err.message || 'Failed to approve team request.');
    } finally {
      setActionRequestId('');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    // clear admin session marker
    try { window.localStorage.removeItem('currentTeam'); } catch (e) {}
    navigate('/');
  };

  if (!user) {
    return (
      <>
        <NavBar pageName="Review Requests" showMenu={false} />
        <Container className="my-4">
          <Row>
            <Col md={8} lg={6} className="mx-auto">
              <Card>
                <Card.Body>
                  <Card.Title>Admin Review</Card.Title>
                  <Card.Text>Please sign in with the admin login to review pending team requests.</Card.Text>
                  <Button onClick={() => navigate('/admin-login')}>Sign in as Admin</Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  if (user && !isAuthorized) {
    return (
      <>
        <NavBar pageName="Review Requests" showMenu={false} />
        <Container className="my-4">
          <Row>
            <Col md={8} lg={6} className="mx-auto">
              <Card>
                <Card.Body>
                  <Card.Title>Not Authorized</Card.Title>
                  <Card.Text>Your email is not authorized to review team requests. Only admin users can access this page.</Card.Text>
                  {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                  <Button variant="secondary" onClick={handleLogout}>Sign Out</Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  return (
    <>
      <NavBar pageName="Review Requests" showMenu={false} />
      <Container className="my-4">
        <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center">
            <h2>Pending Team Requests</h2>
            <div className="d-flex gap-2">
              <Button variant="outline-secondary" size="sm" onClick={() => navigate('/admin-settings')}>Settings</Button>
              <Button variant="outline-secondary" onClick={handleLogout}>Logout</Button>
            </div>
          </Col>
        </Row>

        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <div className="text-center py-4">Loading requests...</div>
        ) : requests.length === 0 ? (
          <Alert variant="info">No pending team requests at this time.</Alert>
        ) : (
          requests.map((req) => (
            <Card className="mb-3" key={req.id}>
              <Card.Body>
                <Card.Title>{req.teamName}</Card.Title>
                <Card.Subtitle className="mb-2 text-muted">{req.contactName} — {req.contactEmail}</Card.Subtitle>
                <Card.Text>{req.note || 'No additional notes provided.'}</Card.Text>
                <div className="d-flex gap-2">
                  <Button
                    variant="success"
                    onClick={() => handleApprove(req.id)}
                    disabled={actionRequestId === req.id}
                  >
                    {actionRequestId === req.id ? 'Approving…' : 'Approve Team'}
                  </Button>
                  <Button variant="secondary" onClick={() => navigate('/')}>Back to Login</Button>
                </div>
              </Card.Body>
            </Card>
          ))
        )}
      </Container>
    </>
  );
};

export default ReviewTeamRequests;
